import { useRef, useState, type FormEvent } from 'react'
import { CircleAlert, LoaderCircle, X } from 'lucide-react'

import { Button } from '../../../ui/Button/Button'
import { ApiError } from '../../../services/api/client'
import {
  createPatientRequestSchema,
  type CreatePatientRequest,
  type ServiceIntent,
} from '../patients-contracts'
import type { PatientAddOutcome } from '../patients-model'
import type { PatientsRepository } from '../patients-repository'
import styles from '../PatientsPage.module.css'
import { usePatientDialog } from '../use-patient-dialog'

type Field = keyof CreatePatientRequest
type Values = Record<Field, string>

const EMPTY: Values = {
  firstName: '',
  lastName: '',
  phoneNumber: '',
  dateOfBirth: '',
  email: '',
  gender: '',
  serviceIntent: '',
}

// Form order, which is also the order invalid fields are listed and focused in.
const FIELD_ORDER: Field[] = [
  'firstName',
  'lastName',
  'dateOfBirth',
  'gender',
  'phoneNumber',
  'email',
  'serviceIntent',
]

const LABELS: Record<Field, string> = {
  firstName: 'First name',
  lastName: 'Last name',
  phoneNumber: 'Phone',
  dateOfBirth: 'Date of birth',
  email: 'Email',
  gender: 'Gender',
  serviceIntent: 'Service context',
}

const FIELD_ERRORS: Record<Field, string> = {
  firstName: "Enter the patient's first name.",
  lastName: "Enter the patient's last name.",
  phoneNumber:
    'Enter a phone number with 8–15 digits, including the area code.',
  dateOfBirth: 'Enter the date of birth.',
  email: 'Enter a valid email address, like name@example.com.',
  gender: 'Select a gender.',
  serviceIntent: 'Select the service context.',
}

/** Separators staff type in phone numbers; the request carries digits only. */
function normalizePhone(value: string) {
  return value.trim().replace(/[\s().-]/g, '')
}

function request(values: Values) {
  return {
    firstName: values.firstName.trim(),
    lastName: values.lastName.trim(),
    phoneNumber: normalizePhone(values.phoneNumber),
    dateOfBirth: values.dateOfBirth.trim(),
    email: values.email.trim(),
    gender: values.gender,
    serviceIntent: values.serviceIntent,
  }
}

/** The patient-create contract's own rules, field by field. */
function validate(values: Values): Partial<Record<Field, string>> {
  const parsed = createPatientRequestSchema.safeParse(request(values))
  if (parsed.success) return {}
  const errors: Partial<Record<Field, string>> = {}
  for (const issue of parsed.error.issues) {
    const field = issue.path[0] as Field | undefined
    if (field && field in FIELD_ERRORS) errors[field] = FIELD_ERRORS[field]
  }
  return errors
}

// Any failure that can follow the NexHealth write: the request timed out or
// lost its answer, or the write needs reconciliation (409/503 "Patient could
// not be created", a 500, a dropped connection). The patient may exist, so
// this never says the patient wasn't created.
const OUTCOME_UNCONFIRMED =
  "Flio couldn't confirm whether the patient was added. The record may already exist: search Patients or the practice system for this patient before trying again."

/** The endpoint's established outcomes, in words staff can act on. Only the
    checks made before anything is sent to NexHealth are definitive. */
function failureCopy(error: unknown) {
  if (!(error instanceof ApiError) || error.code !== 'server')
    return OUTCOME_UNCONFIRMED
  if (error.status === 401 || error.status === 403)
    return "You don't have permission to add patients at this clinic."
  if (error.status === 400)
    return "Some details weren't accepted. Check the phone number, email and date of birth, then try again."
  if (error.status === 409 && error.reason === 'Patient match requires review')
    return 'More than one practice record could be this patient. Find and confirm the patient in your practice system before adding them here.'
  if (error.status === 409 && error.reason === 'Service is unavailable')
    return 'The selected service is no longer available for new patients. Choose another service context and try again.'
  if (
    error.status === 503 &&
    error.reason === 'Patient creation is unavailable'
  )
    return 'Adding patients is unavailable right now. Try again in a few minutes.'
  return OUTCOME_UNCONFIRMED
}

function todayIso() {
  const now = new Date()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  return `${now.getFullYear()}-${month}-${day}`
}

export function AddPatientDialog({
  tenantId,
  repository,
  serviceIntents,
  onClose,
  onCreated,
}: {
  tenantId: string
  repository: PatientsRepository
  serviceIntents: ServiceIntent[]
  onClose: () => void
  onCreated: (patientAuthorityId: string, outcome: PatientAddOutcome) => void
}) {
  const dialog = useRef<HTMLDivElement>(null)
  const summary = useRef<HTMLDivElement>(null)
  const failure = useRef<HTMLParagraphElement>(null)
  const [values, setValues] = useState<Values>(EMPTY)
  const [touched, setTouched] = useState<Partial<Record<Field, boolean>>>({})
  const [attempted, setAttempted] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  usePatientDialog(dialog, { onClose, locked: busy })

  const errors = validate(values)
  const shown = (field: Field) =>
    attempted || (touched[field] && values[field].trim() !== '')
      ? errors[field]
      : undefined
  const listed = attempted ? FIELD_ORDER.filter((field) => errors[field]) : []
  const unavailable = serviceIntents.length === 0

  function update(field: Field, value: string) {
    setValues((current) => ({ ...current, [field]: value }))
    setError(null)
  }

  function focusField(field: Field) {
    document.getElementById(`add-patient-${field}`)?.focus()
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (busy || unavailable) return
    setAttempted(true)
    if (Object.keys(errors).length > 0) {
      // Keyboard and screen reader users land on the list of what to fix.
      window.requestAnimationFrame(() => summary.current?.focus())
      return
    }
    setBusy(true)
    setError(null)
    try {
      const result = await repository.create(
        tenantId,
        createPatientRequestSchema.parse(request(values))
      )
      onCreated(result.patientAuthorityId, result.outcome)
    } catch (caught) {
      setError(failureCopy(caught))
      window.requestAnimationFrame(() => failure.current?.focus())
    } finally {
      setBusy(false)
    }
  }

  function describedBy(field: Field, hint?: boolean) {
    return (
      [
        hint ? `add-patient-${field}-hint` : null,
        shown(field) ? `add-patient-${field}-error` : null,
      ]
        .filter(Boolean)
        .join(' ') || undefined
    )
  }

  function fieldError(field: Field) {
    const message = shown(field)
    return message ? (
      <span className={styles.fieldError} id={`add-patient-${field}-error`}>
        <CircleAlert size={13} aria-hidden="true" /> {message}
      </span>
    ) : null
  }

  const inputProps = (field: Field, hint?: boolean) => ({
    id: `add-patient-${field}`,
    name: field,
    value: values[field],
    required: true,
    'aria-invalid': shown(field) ? true : undefined,
    'aria-describedby': describedBy(field, hint),
    onBlur: () => setTouched((current) => ({ ...current, [field]: true })),
  })

  return (
    <div className={styles.modalBackdrop}>
      <div
        ref={dialog}
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-patient-title"
        aria-describedby="add-patient-description"
      >
        <header className={styles.modalHeader}>
          <div>
            <span className={styles.eyebrow}>Practice record</span>
            <h2 id="add-patient-title">Add patient</h2>
          </div>
          <button
            type="button"
            className={styles.modalClose}
            aria-label="Close Add patient"
            disabled={busy}
            onClick={onClose}
          >
            <X size={18} aria-hidden="true" />
          </button>
        </header>
        <form
          className={styles.patientForm}
          noValidate
          aria-busy={busy}
          onSubmit={(event) => void submit(event)}
        >
          <p id="add-patient-description" className={styles.formIntro}>
            Flio checks for an exact existing patient before creating a new
            NexHealth record. All fields are required.
          </p>
          {listed.length > 0 ? (
            <div
              ref={summary}
              className={styles.errorSummary}
              role="alert"
              tabIndex={-1}
              aria-labelledby="add-patient-error-summary"
            >
              <strong id="add-patient-error-summary">
                {listed.length === 1
                  ? '1 field needs attention'
                  : `${listed.length} fields need attention`}
              </strong>
              <ul>
                {listed.map((field) => (
                  <li key={field}>
                    <a
                      href={`#add-patient-${field}`}
                      onClick={(event) => {
                        event.preventDefault()
                        focusField(field)
                      }}
                    >
                      {LABELS[field]}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          <fieldset className={styles.formSection}>
            <legend>Patient</legend>
            <div className={styles.formGrid}>
              <div className={styles.formField}>
                <label htmlFor="add-patient-firstName">First name</label>
                <input
                  {...inputProps('firstName')}
                  maxLength={80}
                  autoComplete="off"
                  autoFocus
                  data-autofocus
                  onChange={(event) => update('firstName', event.target.value)}
                />
                {fieldError('firstName')}
              </div>
              <div className={styles.formField}>
                <label htmlFor="add-patient-lastName">Last name</label>
                <input
                  {...inputProps('lastName')}
                  maxLength={80}
                  autoComplete="off"
                  onChange={(event) => update('lastName', event.target.value)}
                />
                {fieldError('lastName')}
              </div>
              <div className={styles.formField}>
                <label htmlFor="add-patient-dateOfBirth">Date of birth</label>
                <input
                  {...inputProps('dateOfBirth')}
                  type="date"
                  max={todayIso()}
                  autoComplete="off"
                  onChange={(event) =>
                    update('dateOfBirth', event.target.value)
                  }
                />
                {fieldError('dateOfBirth')}
              </div>
              <div className={styles.formField}>
                <label htmlFor="add-patient-gender">Gender</label>
                <select
                  {...inputProps('gender')}
                  onChange={(event) => update('gender', event.target.value)}
                >
                  <option value="" disabled>
                    Select
                  </option>
                  <option>Female</option>
                  <option>Male</option>
                  <option>Other</option>
                </select>
                {fieldError('gender')}
              </div>
            </div>
          </fieldset>
          <fieldset className={styles.formSection}>
            <legend>Contact</legend>
            <div className={styles.formGrid}>
              <div className={styles.formField}>
                <label htmlFor="add-patient-phoneNumber">Phone</label>
                <input
                  {...inputProps('phoneNumber', true)}
                  type="tel"
                  inputMode="tel"
                  autoComplete="off"
                  placeholder="(516) 555-0123"
                  onChange={(event) =>
                    update('phoneNumber', event.target.value)
                  }
                />
                <span
                  className={styles.fieldHint}
                  id="add-patient-phoneNumber-hint"
                >
                  Include the area code.
                </span>
                {fieldError('phoneNumber')}
              </div>
              <div className={styles.formField}>
                <label htmlFor="add-patient-email">Email</label>
                <input
                  {...inputProps('email')}
                  type="email"
                  maxLength={254}
                  autoComplete="off"
                  onChange={(event) => update('email', event.target.value)}
                />
                {fieldError('email')}
              </div>
            </div>
          </fieldset>
          <fieldset className={styles.formSection}>
            <legend>Visit</legend>
            <div className={styles.formField}>
              <label htmlFor="add-patient-serviceIntent">Service context</label>
              <select
                {...inputProps('serviceIntent')}
                disabled={unavailable}
                onChange={(event) =>
                  update('serviceIntent', event.target.value)
                }
              >
                <option value="" disabled>
                  Select service
                </option>
                {serviceIntents.map((item) => (
                  <option key={item.serviceIntent} value={item.serviceIntent}>
                    {item.serviceLabel}
                  </option>
                ))}
              </select>
              {fieldError('serviceIntent')}
            </div>
          </fieldset>
          {unavailable ? (
            <p className={styles.formNotice} role="status">
              <CircleAlert size={15} aria-hidden="true" />
              Patient creation is unavailable until a reviewed service mapping
              is active.
            </p>
          ) : null}
          {error ? (
            <p
              ref={failure}
              className={styles.formError}
              role="alert"
              tabIndex={-1}
            >
              <CircleAlert size={15} aria-hidden="true" />
              {error}
            </p>
          ) : null}
          <footer className={styles.modalActions}>
            <Button
              type="button"
              variant="ghost"
              disabled={busy}
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={busy || unavailable}>
              {busy ? (
                <>
                  <LoaderCircle
                    className={styles.buttonSpinner}
                    size={16}
                    aria-hidden="true"
                  />
                  Checking patient…
                </>
              ) : (
                'Create patient'
              )}
            </Button>
          </footer>
        </form>
      </div>
    </div>
  )
}
